import { OtsMediaCarouselDialog } from "../components/OtsMediaCarouselDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../theme/OtsTheme";
import { formatActivityDateTime } from "./driver-activity-format";
import type { ActivityAttachment, ActivityEntry } from "./driver-activity-view-model";

export type ActivityAttachmentsDialogProps = {
  /** The ticket whose attachments are shown; the dialog is closed without one. */
  entry?: ActivityEntry;
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  timeZone?: string;
  /** Downloads one attachment, or all of the ticket's when called with several. Hides the buttons when missing. */
  onDownload?: (attachments: ActivityAttachment[], entry: ActivityEntry) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** A ticket's pictures and documents in a carousel, with single and bulk download. */
export const ActivityAttachmentsDialog = ({ entry, index, onIndexChange, onClose, timeZone, onDownload, brand, colorScheme }: ActivityAttachmentsDialogProps) => {
  const attachments = entry?.attachments ?? [];

  return (
    <OtsMediaCarouselDialog
      open={attachments.length > 0}
      onClose={onClose}
      title={`Attachments · ${entry?.resourceId ?? "ticket"}`}
      index={index}
      onIndexChange={onIndexChange}
      brand={brand}
      colorScheme={colorScheme}
      items={attachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        kind: attachment.kind,
        src: attachment.kind === "image" ? attachment.url : undefined,
        caption: [
          attachment.fileName !== attachment.name && attachment.fileName,
          attachment.personName,
          attachment.createDttm != null && formatActivityDateTime(attachment.createDttm, timeZone),
        ].filter(Boolean).join(" · ") || undefined,
      }))}
      onDownload={entry && onDownload && ((item) => {
        const attachment = attachments.find((candidate) => candidate.id === item.id);
        if (attachment) onDownload([attachment], entry);
      })}
      onDownloadAll={entry && onDownload && (() => onDownload(attachments, entry))}/>
  );
};
